/**
 * Fábrica de Vídeos — cria o vídeo de anúncio (4 módulos de 5 s) DENTRO do BI.
 *
 * v2 (decisão da Mariana, 24/09/2026): a tela gera o vídeo pela API paga do Higgsfield (serviço
 * services/fabrica-videos, 127.0.0.1:8105) e mostra créditos e preço ANTES de cada etapa. Ordem:
 * produto → roteiro (insumos reais do anúncio) → cena → GERAR (estimativa grátis → aprovar
 * roteiro → cena → 4 módulos → montagem). Cada botão que gasta pede confirmação.
 *
 * O caminho da v1 (colar o briefing no Claude com o conector Higgsfield) continua disponível na
 * última etapa, recolhido, para quem preferir usar os créditos do plano.
 *
 * Fora do SourceScopeGuard: fala com o bridge (busca de anúncio e insumos do roteiro, opcionais)
 * e com o serviço 8105. O rascunho do briefing fica neste navegador (localStorage); o projeto
 * (cena, clipes, custos) fica no serviço.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Link,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Step,
  StepButton,
  Stepper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { EtapaGeracao } from './fabricaVideos/EtapaGeracao'
import { fetchProjetos, fetchStatus, fetchProjeto, type ResumoProjeto, type StatusServico } from './fabricaVideos/fabricaVideosService'
import { fetchProdutosVendidos, fetchRoteiroInsumos, type RoteiroInsumos } from '../api/bridge'
import { linkAnuncio, normalizarLinhas, type LinhaAnuncio } from './vendasAnuncio/logic'
import {
  AMBIENTES,
  APRESENTADORAS,
  CHAVE_RASCUNHO,
  DOLAR_PADRAO_ORIGEM,
  MAX_PALAVRAS_MODULO,
  MODELOS_FALA,
  MODULOS,
  PLANOS,
  ROTULO_ETAPA,
  aplicarModelo,
  briefingPronto,
  briefingVazio,
  checarElemento,
  checarFala,
  checarProduto,
  contarPalavras,
  custoVideo,
  fmtBrlBr,
  fmtCred,
  fmtUsdBr,
  lerRascunho,
  montarPedido,
  nomeElementoSugerido,
  normalizarMlb,
  pedidoPronto,
  pistasPorModulo,
  resumirInsumos,
  roteiroOk,
  rotuloFonte,
  segundosFala,
  type AmbienteId,
  type ApresentadoraId,
  type Briefing,
  type Checagem,
  type ContaMl,
  type ModuloId,
  type PlanoId,
  type Pista,
} from './fabricaVideos/logic'

const ETAPAS = ['Produto', 'Roteiro', 'Cena', 'Gerar'] as const

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const um = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

function lerLocal(): Briefing {
  try {
    return lerRascunho(localStorage.getItem(CHAVE_RASCUNHO))
  } catch {
    return briefingVazio()
  }
}

function Checagens({ itens }: { itens: Checagem[] }) {
  if (itens.length === 0) return null
  return (
    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
      {itens.map((c) => (
        <Typography
          key={c.texto}
          variant="caption"
          sx={{ color: c.nivel === 'erro' ? 'error.main' : 'warning.main', display: 'block' }}
        >
          {c.nivel === 'erro' ? '✕ ' : '! '}
          {c.texto}
        </Typography>
      ))}
    </Stack>
  )
}

// ---------------------------------------------------------------- etapa 1

function BuscaAnuncio({ onEscolher }: { onEscolher: (l: LinhaAnuncio) => void }) {
  const [busca, setBusca] = useState('')
  const [linhas, setLinhas] = useState<LinhaAnuncio[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const buscar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const r = await fetchProdutosVendidos({ periodo: '30d' }, undefined, {
        pagina: 1,
        limite: 12,
        busca: busca.trim() || undefined,
        ordenarPor: 'receita',
      })
      // Só Mercado Livre: o Elemento nasce da foto do anúncio e o CTA fala do link do ML.
      setLinhas(normalizarLinhas(r.produtos).filter((l) => /^MLB\d+$/i.test(l.id)))
    } catch (e) {
      setLinhas(null)
      setErro(
        e instanceof Error
          ? `A busca no BI não respondeu (${e.message}). Preencha o anúncio à mão abaixo — a tela funciona igual.`
          : 'A busca no BI não respondeu. Preencha o anúncio à mão abaixo.',
      )
    } finally {
      setCarregando(false)
    }
  }, [busca])

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          fullWidth
          label="Buscar anúncio vendido nos últimos 30 dias"
          placeholder="título, SKU ou MLB"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') buscar()
          }}
        />
        <Button variant="outlined" onClick={buscar} disabled={carregando} sx={{ flexShrink: 0 }}>
          {carregando ? <CircularProgress size={18} /> : 'Buscar'}
        </Button>
      </Stack>
      {erro ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {erro}
        </Alert>
      ) : null}
      {linhas && linhas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nenhum anúncio do Mercado Livre com venda nos últimos 30 dias bateu com a busca.
        </Typography>
      ) : null}
      {linhas && linhas.length > 0 ? (
        <List dense sx={{ mt: 1, maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {linhas.map((l) => (
            <ListItemButton key={l.id} onClick={() => onEscolher(l)}>
              <ListItemAvatar>
                <Avatar variant="rounded" src={l.thumbnail || undefined} alt="">
                  {l.thumbnail ? null : '—'}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={l.titulo || l.descricao || l.id}
                secondary={`${l.id}${l.sku ? ` · SKU ${l.sku}` : ''} · ${l.unidades.toLocaleString('pt-BR')} un em 30 dias`}
              />
            </ListItemButton>
          ))}
        </List>
      ) : null}
    </Box>
  )
}

function EtapaProduto({ b, set }: { b: Briefing; set: (f: (x: Briefing) => Briefing) => void }) {
  const p = b.produto
  const setP = (patch: Partial<Briefing['produto']>) => set((x) => ({ ...x, produto: { ...x.produto, ...patch } }))
  return (
    <Stack spacing={2.5}>
      <BuscaAnuncio
        onEscolher={(l) =>
          set((x) => ({
            ...x,
            produto: {
              ...x.produto,
              mlb: l.id.toUpperCase(),
              titulo: l.titulo || l.descricao || '',
              link: l.link || linkAnuncio(l.id) || '',
              thumbnail: l.thumbnail || '',
            },
            elemento: {
              ...x.elemento,
              nome: x.elemento.nome || nomeElementoSugerido(l.titulo || l.descricao || ''),
            },
          }))
        }
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
        {p.thumbnail ? (
          <Box
            component="img"
            src={p.thumbnail}
            alt=""
            sx={{ width: 96, height: 96, objectFit: 'contain', borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          />
        ) : null}
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label="MLB do anúncio"
              value={p.mlb}
              onChange={(e) => setP({ mlb: e.target.value.toUpperCase() })}
              onBlur={(e) => {
                const mlb = normalizarMlb(e.target.value)
                setP({ mlb, link: p.link || linkAnuncio(mlb) || '' })
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="fv-conta">Conta</InputLabel>
              <Select
                labelId="fv-conta"
                label="Conta"
                value={p.conta}
                onChange={(e) => setP({ conta: e.target.value as ContaMl })}
              >
                <MenuItem value="">Não sei — procurar nas duas</MenuItem>
                <MenuItem value="FIESTAPARTYFESTA">FIESTAPARTYFESTA (Matriz)</MenuItem>
                <MenuItem value="FIESTAPARTYHOME">FIESTAPARTYHOME (Extrema)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <TextField size="small" label="Título do anúncio" value={p.titulo} onChange={(e) => setP({ titulo: e.target.value })} />
          <TextField size="small" label="Link do anúncio (opcional)" value={p.link} onChange={(e) => setP({ link: e.target.value })} />
          <Checagens itens={checarProduto(p)} />
        </Stack>
      </Stack>

    </Stack>
  )
}

// ---------------------------------------------------------------- etapa 2

const MLB_OK = /^MLB\d{6,}$/

function PistaChip({ p }: { p: Pista }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        border: 1,
        borderColor: p.tipo === 'real' ? 'divider' : 'warning.main',
        bgcolor: 'background.paper',
        maxWidth: '100%',
      }}
    >
      <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
        {p.texto}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {p.tipo === 'real' ? 'dado real' : 'hipótese'} · {rotuloFonte(p.fonte)}
        {p.detalhe ? ` · ${p.detalhe}` : ''}
      </Typography>
    </Box>
  )
}

function ListaTextos({ itens, vazio }: { itens: Array<{ texto: string; sub?: string }>; vazio: string }) {
  if (itens.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {vazio}
      </Typography>
    )
  }
  return (
    <Stack spacing={0.5}>
      {itens.map((i, k) => (
        <Box key={`${k}-${i.texto.slice(0, 24)}`}>
          <Typography variant="body2">{i.texto}</Typography>
          {i.sub ? (
            <Typography variant="caption" color="text.secondary">
              {i.sub}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Stack>
  )
}

/**
 * Insumos do roteiro: pesquisa da Fábrica de Anúncio + o próprio anúncio (ficha, avaliações,
 * perguntas, campeões), lidos pelo bridge. É daqui que as falas têm que sair — não de modelo.
 * O resumo (pistas por módulo) fica no briefing e vai no pedido; o detalhe é só desta tela.
 */
function InsumosRoteiro({ b, set }: { b: Briefing; set: (f: (x: Briefing) => Briefing) => void }) {
  const mlb = b.produto.mlb
  const [dados, setDados] = useState<RoteiroInsumos | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState(false)

  const carregar = useCallback(
    async (refresh = false) => {
      if (!MLB_OK.test(mlb)) return
      setCarregando(true)
      setErro(null)
      try {
        const r = await fetchRoteiroInsumos(mlb, { refresh })
        setDados(r)
        set((x) => ({ ...x, insumos: resumirInsumos(r) }))
      } catch (e) {
        setDados(null)
        setErro(e instanceof Error ? e.message : 'o bridge não respondeu')
      } finally {
        setCarregando(false)
      }
    },
    [mlb, set],
  )

  // Carrega uma vez por MLB. Se o rascunho já tem o resumo deste MLB, não bate no bridge de novo.
  const resumoEhDesteMlb = b.insumos?.mlb === mlb
  useEffect(() => {
    if (MLB_OK.test(mlb) && !resumoEhDesteMlb && !dados) carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mlb, resumoEhDesteMlb])

  if (!MLB_OK.test(mlb)) {
    return (
      <Alert severity="info">
        Escolha o anúncio na etapa 1 para carregar os insumos do roteiro (pesquisa, ficha, avaliações e perguntas).
      </Alert>
    )
  }

  const r = b.insumos && resumoEhDesteMlb ? b.insumos : null
  const an = dados?.anuncio
  const av = dados?.avaliacoes
  const pg = dados?.perguntas
  const pesq = dados?.pesquisa

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 160 }}>
          Insumos do roteiro · {mlb}
        </Typography>
        <Button size="small" onClick={() => carregar(true)} disabled={carregando}>
          {carregando ? <CircularProgress size={16} /> : 'Reler na API'}
        </Button>
        {dados ? (
          <Button size="small" onClick={() => setAberto((v) => !v)}>
            {aberto ? 'Esconder detalhe' : 'Ver detalhe'}
          </Button>
        ) : null}
      </Stack>

      {erro ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Não deu para ler os insumos ({erro}). O pedido vai avisar o Claude para ler o anúncio, as avaliações e as
          perguntas pela API antes de propor as falas.
        </Alert>
      ) : null}

      {r ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip
            size="small"
            color={r.pesquisaExiste ? 'success' : 'warning'}
            variant="outlined"
            label={r.pesquisaExiste ? `Pesquisa da Fábrica de Anúncio: ${r.pesquisaData || 'sem data'}` : 'Sem pesquisa da Fábrica de Anúncio'}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${r.avaliacoesTotal.toLocaleString('pt-BR')} avaliações${r.avaliacoesMedia != null ? ` · nota ${r.avaliacoesMedia.toLocaleString('pt-BR')}` : ''}`}
          />
          <Chip size="small" variant="outlined" label={`${r.perguntasTotal.toLocaleString('pt-BR')} perguntas`} />
          <Chip size="small" variant="outlined" label={`${r.campeoes} campeões do catálogo`} />
          <Chip
            size="small"
            variant="outlined"
            label={`lido ${r.geradoEm ? new Date(r.geradoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—'}${r.fonte === 'cache' ? ' (cache)' : ''}`}
          />
        </Stack>
      ) : null}

      {r && !r.pesquisaExiste ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Não há pesquisa de mercado salva para este anúncio. Rode <b>/fabricadeanuncio</b> primeiro para ter objeções, elogios,
          diferenciais e faixa de preço dos campeões. Até lá, as pistas abaixo vêm só do próprio anúncio.
        </Alert>
      ) : null}
      {r && r.pesquisaAviso && r.pesquisaExiste ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {r.pesquisaAviso}
        </Alert>
      ) : null}
      {r && r.avisos.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Avisos do bridge: {r.avisos.join(' · ')}
        </Typography>
      ) : null}

      {aberto && dados ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {an ? (
            <Box>
              <Typography variant="subtitle2">Ficha do anúncio</Typography>
              <Typography variant="body2">
                {an.titulo}
                {an.preco != null ? ` · ${brl(an.preco)}` : ''} · {an.vendidos.toLocaleString('pt-BR')} vendidos
                {an.conta ? ` · ${an.conta}` : ''}
                {an.catalogo ? ' · catálogo' : ' · sem catálogo'}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {an.atributos.map((a) => (
                  <Chip key={a.id || a.nome} size="small" label={`${a.nome}: ${a.valor}`} />
                ))}
              </Stack>
              {an.descricao ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {an.descricao}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {pesq?.existe ? (
            <Box>
              <Typography variant="subtitle2">Pesquisa da Fábrica de Anúncio ({pesq.data_pesquisa || 'sem data'})</Typography>
              <ListaTextos
                itens={[
                  ...(pesq.objecoes || []).map((o) => ({ texto: `Objeção: ${o.texto}`, sub: [o.fonte, o.quantidade ? `${o.quantidade}×` : ''].filter(Boolean).join(' · ') })),
                  ...(pesq.elogios || []).map((o) => ({ texto: `Elogio: ${o.texto}`, sub: o.fonte })),
                  ...(pesq.diferenciais || []).map((o) => ({ texto: `Diferencial: ${o.texto}`, sub: o.fonte })),
                ]}
                vazio="A pesquisa existe, mas não trouxe objeções, elogios nem diferenciais."
              />
            </Box>
          ) : null}

          <Box>
            <Typography variant="subtitle2">
              Avaliações {av?.disponivel ? `· ${av.total.toLocaleString('pt-BR')}${av.media != null ? ` · nota ${av.media.toLocaleString('pt-BR')}` : ''}` : ''}
            </Typography>
            {av?.disponivel ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    O que decepciona (notas 1 e 2)
                  </Typography>
                  <ListaTextos
                    itens={av.negativas.map((x) => ({ texto: x.texto || x.titulo, sub: `nota ${x.nota} · ${x.curtidas} curtidas · ${x.data}` }))}
                    vazio="Nenhuma avaliação negativa com texto."
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    O que encanta (notas 4 e 5)
                  </Typography>
                  <ListaTextos
                    itens={av.positivas.map((x) => ({ texto: x.texto || x.titulo, sub: `nota ${x.nota} · ${x.curtidas} curtidas · ${x.data}` }))}
                    vazio="Nenhuma avaliação positiva com texto."
                  />
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                A API não devolveu avaliações deste anúncio.
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2">Perguntas {pg?.disponivel ? `· ${pg.total.toLocaleString('pt-BR')}` : ''}</Typography>
            {pg?.disponivel ? (
              <>
                {pg.temas.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ my: 0.5 }}>
                    {pg.temas.map((t) => (
                      <Chip key={t.tema} size="small" color="warning" variant="outlined" label={`${t.quantidade}× "${t.tema}"`} title={t.exemplos.join(' | ')} />
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      temas por palavra repetida (hipótese)
                    </Typography>
                  </Stack>
                ) : null}
                <ListaTextos
                  itens={pg.lista.slice(0, 8).map((q) => ({ texto: q.pergunta, sub: q.resposta ? `→ ${q.resposta}` : 'sem resposta' }))}
                  vazio="Nenhuma pergunta neste anúncio."
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Perguntas indisponíveis (precisam do token da conta no sync).
              </Typography>
            )}
          </Box>

          {dados.campeoes && dados.campeoes.length ? (
            <Box>
              <Typography variant="subtitle2">Campeões do mesmo catálogo</Typography>
              <ListaTextos
                itens={dados.campeoes.map((c) => ({
                  texto: `${c.titulo || c.mlb}${c.preco != null ? ` · ${brl(c.preco)}` : ''} · ${c.vendidos.toLocaleString('pt-BR')} vendidos${c.avaliacoes.media != null ? ` · nota ${c.avaliacoes.media.toLocaleString('pt-BR')} (${c.avaliacoes.total})` : ''}`,
                  sub: [
                    ...c.avaliacoes.negativas.slice(0, 2).map((n) => `decepciona: ${n.texto || n.titulo}`),
                    ...c.perguntas.temas.slice(0, 2).map((t) => `${t.quantidade}× "${t.tema}"`),
                  ].join(' · '),
                }))}
                vazio=""
              />
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  )
}

function EtapaRoteiro({ b, set }: { b: Briefing; set: (f: (x: Briefing) => Briefing) => void }) {
  const setFala = (id: ModuloId, v: string) => set((x) => ({ ...x, roteiro: { ...x.roteiro, [id]: v } }))
  const pistas = b.insumos && b.insumos.mlb === b.produto.mlb ? b.insumos.pistas : pistasPorModulo(null)
  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        As falas saem dos insumos abaixo, não de modelo genérico: hook = objeção nº 1 ou ocasião; característica = diferencial
        e ficha; close = a prova que derruba a objeção; CTA = ação no link, com preço só se a faixa sustentar. Cada pista diz
        se é dado real ou hipótese e de onde veio.
      </Typography>
      <InsumosRoteiro b={b} set={set} />
      <TextField
        size="small"
        label="Como a apresentadora chama o produto"
        placeholder="essas taças de champanhe"
        value={b.chamada}
        onChange={(e) => set((x) => ({ ...x, chamada: e.target.value }))}
        helperText="Usado só para preencher os modelos de fala. Curto: até 4 palavras."
      />
      {MODULOS.map((m) => {
        const texto = b.roteiro[m.id]
        const n = contarPalavras(texto)
        const passou = n > MAX_PALAVRAS_MODULO
        const ps = pistas[m.id]
        return (
          <Box key={m.id}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2">
                {m.n}. {m.nome}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {m.papel}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Na tela: {m.oQueAparece}
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={1}
              value={texto}
              onChange={(e) => setFala(m.id, e.target.value)}
              placeholder="O que ela fala neste módulo"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip
                      size="small"
                      color={n === 0 ? 'default' : passou ? 'error' : 'success'}
                      variant="outlined"
                      label={`${n}/${MAX_PALAVRAS_MODULO} · ${um(segundosFala(n))} s`}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {ps.length ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Pistas para esta fala ({ps.length}):
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {ps.slice(0, 6).map((p, k) => (
                    <PistaChip key={`${p.fonte}-${k}`} p={p} />
                  ))}
                </Stack>
              </Box>
            ) : (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75 }}>
                Sem pista para este módulo: os modelos abaixo são genéricos. Prefira escrever a partir do anúncio.
              </Typography>
            )}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {MODELOS_FALA[m.id].map((modelo) => {
                const f = aplicarModelo(modelo, b.chamada)
                return (
                  <Chip key={modelo} size="small" label={f} onClick={() => setFala(m.id, f)} sx={{ maxWidth: '100%' }} />
                )
              })}
            </Stack>
            <Checagens itens={texto.trim() ? checarFala(texto) : []} />
          </Box>
        )
      })}
    </Stack>
  )
}

// ---------------------------------------------------------------- etapa 3

function EtapaCena({ b, set }: { b: Briefing; set: (f: (x: Briefing) => Briefing) => void }) {
  const setC = (patch: Partial<Briefing['cena']>) => set((x) => ({ ...x, cena: { ...x.cena, ...patch } }))
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        A cena base é uma imagem só (a apresentadora segurando o produto) que vira o primeiro quadro dos 4 vídeos: mesma
        pessoa, mesma roupa, mesma casa. Custa 2 créditos e você aprova antes de qualquer vídeo.
      </Typography>
      <FormControl size="small" fullWidth>
        <InputLabel id="fv-apr">Apresentadora</InputLabel>
        <Select
          labelId="fv-apr"
          label="Apresentadora"
          value={b.cena.apresentadora}
          onChange={(e) => setC({ apresentadora: e.target.value as ApresentadoraId })}
        >
          {Object.entries(APRESENTADORAS).map(([id, a]) => (
            <MenuItem key={id} value={id}>
              {a.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth>
        <InputLabel id="fv-amb">Ambiente</InputLabel>
        <Select
          labelId="fv-amb"
          label="Ambiente"
          value={b.cena.ambiente}
          onChange={(e) => setC({ ambiente: e.target.value as AmbienteId })}
        >
          {Object.entries(AMBIENTES).map(([id, a]) => (
            <MenuItem key={id} value={id}>
              {a.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Módulo 2 — o que ela mostra ao girar o produto"
        placeholder="as seis taças, a haste longa, o cristal transparente"
        value={b.cena.partes}
        onChange={(e) => setC({ partes: e.target.value })}
      />
      <TextField
        size="small"
        label="Módulo 3 — o que aparece no close"
        placeholder="o brilho do cristal, a borda fininha"
        value={b.cena.detalhesVisuais}
        onChange={(e) => setC({ detalhesVisuais: e.target.value })}
      />
      <TextField
        size="small"
        label="Módulo 4 — caixa ou kit na mesa"
        placeholder="a caixa do jogo com seis taças"
        value={b.cena.embalagem}
        onChange={(e) => setC({ embalagem: e.target.value })}
      />
      <TextField
        size="small"
        label="Ajuste na cena (opcional)"
        placeholder="cabelo preso, mais luz no produto"
        value={b.cena.ajuste}
        onChange={(e) => setC({ ajuste: e.target.value })}
      />
    </Stack>
  )
}

// ---------------------------------------------------------------- etapa 4

function EtapaPedido({ b, set }: { b: Briefing; set: (f: (x: Briefing) => Briefing) => void }) {
  const p = b.produto
  const setE = (patch: Partial<Briefing['elemento']>) => set((x) => ({ ...x, elemento: { ...x.elemento, ...patch } }))
  const setCusto = (patch: Partial<Briefing['custo']>) => set((x) => ({ ...x, custo: { ...x.custo, ...patch } }))
  const custo = custoVideo(b.custo)
  const pronto = pedidoPronto(b)
  const texto = useMemo(() => montarPedido(b), [b])
  const [copiado, setCopiado] = useState<'ok' | 'falhou' | null>(null)

  const pendencias = [
    ...checarProduto(b.produto).filter((c) => c.nivel === 'erro').map((c) => `Produto: ${c.texto}`),
    ...checarElemento(b.elemento.nome, b.elemento.id).filter((c) => c.nivel === 'erro').map((c) => `Elemento: ${c.texto}`),
    ...(roteiroOk(b.roteiro) ? [] : ['Roteiro: tem fala vazia ou acima de 12 palavras.']),
  ]

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado('ok')
    } catch {
      setCopiado('falhou')
    }
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Elemento de referência no Higgsfield
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          É ele que mantém o produto idêntico ao anúncio em todas as gerações. Se ainda não existe, deixe o ID em branco:
          o Claude cria o Elemento com a foto principal do anúncio antes de gerar qualquer coisa.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label="Nome do Elemento"
            value={b.elemento.nome}
            onChange={(e) => setE({ nome: e.target.value })}
            helperText={`${b.elemento.nome.length}/32 · minúsculas e hífen`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={() => setE({ nome: nomeElementoSugerido(p.titulo) })} disabled={!p.titulo.trim()}>
                    Sugerir
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <TextField
            size="small"
            fullWidth
            label="ID do Elemento (se já existe)"
            placeholder="1c21c2a4-74d7-4261-9a1b-fcfa9d032a5b"
            value={b.elemento.id}
            onChange={(e) => setE({ id: e.target.value.trim() })}
          />
        </Stack>
        <Checagens itens={checarElemento(b.elemento.nome, b.elemento.id)} />
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="fv-plano">Plano do Higgsfield</InputLabel>
          <Select
            labelId="fv-plano"
            label="Plano do Higgsfield"
            value={b.custo.plano}
            onChange={(e) => setCusto({ plano: e.target.value as PlanoId })}
          >
            {Object.entries(PLANOS).map(([id, pl]) => (
              <MenuItem key={id} value={id}>
                {pl.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="number"
          label="Dólar (R$)"
          value={b.custo.dolar}
          onChange={(e) => setCusto({ dolar: Number(e.target.value) })}
          inputProps={{ step: 0.01, min: 0 }}
          helperText={`Valor inicial: ${DOLAR_PADRAO_ORIGEM}. Troque pela cotação do dia.`}
          sx={{ maxWidth: 260 }}
        />
        <FormControlLabel
          control={<Switch checked={b.custo.iof} onChange={(e) => setCusto({ iof: e.target.checked })} />}
          label="Somar IOF de cartão internacional (3,5%)"
        />
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Estimativa pela tabela do manual: cena base 2 créditos + 4 módulos × 45 créditos (Seedance 2.0, 1080p, 5 s) + montagem
        0 = <b>{custo.creditos} créditos ≈ {brl(custo.brl)}</b>. Cada módulo refeito soma {custo.creditosRefazerModulo} créditos (≈{' '}
        {brl(custo.brlRefazerModulo)}). O custo real sai do extrato do Higgsfield no fim, e o Claude informa.
      </Typography>

      {pendencias.length > 0 ? (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Falta resolver antes de copiar:
          </Typography>
          {pendencias.map((p) => (
            <Typography key={p} variant="body2">
              • {p}
            </Typography>
          ))}
        </Alert>
      ) : null}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Pedido para colar no Claude
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            fontSize: '0.8rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 380,
            overflow: 'auto',
            opacity: pronto ? 1 : 0.6,
          }}
        >
          {texto}
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
          <Button variant="contained" onClick={copiar} disabled={!pronto}>
            Copiar pedido
          </Button>
          <Button component={Link} href="https://claude.ai/new" target="_blank" rel="noopener" variant="outlined">
            Abrir o Claude
          </Button>
          {copiado === 'ok' ? (
            <Typography variant="body2" color="success.main">
              Copiado. Cole numa conversa com o conector Higgsfield AI ligado.
            </Typography>
          ) : null}
          {copiado === 'falhou' ? (
            <Typography variant="body2" color="error.main">
              O navegador bloqueou a cópia — selecione o texto acima e copie à mão.
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  )
}

// ---------------------------------------------------------------- página

/** Projetos salvos no serviço: retomar um vídeo em andamento sem refazer o briefing. */
function ProjetosSalvos({ atual, onAbrir }: { atual: string; onAbrir: (id: string) => void }) {
  const [lista, setLista] = useState<ResumoProjeto[] | null>(null)
  useEffect(() => {
    fetchProjetos()
      .then((r) => setLista(r.projetos))
      .catch(() => setLista([]))
  }, [atual])
  if (!lista || lista.length === 0) return null
  return (
    <Accordion disableGutters sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<span aria-hidden>▾</span>}>
        <Typography variant="subtitle2">Vídeos salvos ({lista.length})</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
          {lista.map((l) => (
            <ListItemButton key={l.id} selected={l.id === atual} onClick={() => onAbrir(l.id)}>
              <ListItemText
                primary={`${l.mlb} — ${l.titulo || 'sem título'}`}
                secondary={`${ROTULO_ETAPA[l.etapa]} · ${l.modulos_aprovados}/4 módulos · gasto ${fmtUsdBr(l.custo_usd)} (${fmtBrlBr(l.custo_brl)}) · ${new Date(l.atualizado_em).toLocaleString('pt-BR')}`}
              />
            </ListItemButton>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

export function FabricaVideosPage() {
  const theme = useTheme()
  const estreito = useMediaQuery(theme.breakpoints.down('md'))
  const [b, setB] = useState<Briefing>(lerLocal)
  const [etapa, setEtapa] = useState(0)
  const [status, setStatus] = useState<StatusServico | null>(null)
  const [statusErro, setStatusErro] = useState<string | null>(null)

  const set = useCallback((f: (x: Briefing) => Briefing) => setB((x) => f(x)), [])

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_RASCUNHO, JSON.stringify(b))
    } catch {
      /* navegador sem storage: o rascunho só não sobrevive ao recarregar */
    }
  }, [b])

  const carregarStatus = useCallback(() => {
    fetchStatus()
      .then((s) => {
        setStatus(s)
        setStatusErro(null)
      })
      .catch((e) => setStatusErro(e instanceof Error ? e.message : 'serviço indisponível'))
  }, [])
  useEffect(() => {
    carregarStatus()
  }, [carregarStatus, etapa])

  const abrirProjeto = useCallback(async (id: string) => {
    try {
      const r = await fetchProjeto(id)
      const q = r.projeto
      setB((x) => ({
        ...x,
        projetoId: q.id,
        produto: { ...x.produto, ...q.produto },
        chamada: q.chamada,
        roteiro: q.roteiro,
        cena: {
          ...x.cena,
          apresentadora: q.cena.apresentadora,
          ambiente: q.cena.ambiente,
          partes: q.cena.partes,
          detalhesVisuais: q.cena.detalhesVisuais,
          embalagem: q.cena.embalagem,
          ajuste: q.cena.ajuste,
        },
        insumos: q.insumos,
      }))
      setEtapa(3)
    } catch {
      /* projeto sumiu: a lista recarrega sozinha */
    }
  }, [])

  const feitas = [checarProduto(b.produto).every((c) => c.nivel !== 'erro'), roteiroOk(b.roteiro), true, briefingPronto(b) && !!b.projetoId]

  const palavras = MODULOS.reduce((a, m) => a + contarPalavras(b.roteiro[m.id]), 0)
  const c30 = status?.custos_30d

  return (
    <DomainPageShell
      title="Fábrica de Vídeos"
      subtitle="Anúncio do Mercado Livre → vídeo vertical de 20 s com apresentadora, gerado aqui pela API do Higgsfield. Preço antes de cada etapa."
      stats={[
        {
          label: 'Higgsfield',
          value: status ? (status.higgsfield.chave ? 'pronto' : 'sem chave') : statusErro ? 'fora' : '…',
          hint: status
            ? `${status.ffmpeg.ffmpeg ? 'ffmpeg ok' : 'sem ffmpeg'} · ${status.texto.chave ? 'IA de roteiro ok' : 'sem IA de roteiro'}`
            : statusErro || 'consultando o serviço',
        },
        {
          label: 'Dólar',
          value: status ? status.dolar.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '…',
          hint: status ? `${status.dolar.origem}${status.dolar.data ? ` · ${status.dolar.data}` : ''} · IOF ${status.dolar.iof_pct}%` : '',
        },
        {
          label: 'Gasto em 30 dias',
          value: c30 ? fmtBrlBr(c30.brl) : '…',
          hint: c30 ? `${fmtCred(c30.credits)} · ${fmtUsdBr(c30.usd)} · ${c30.geracoes} gerações${c30.falhas ? ` · ${c30.falhas} falhas (não cobradas)` : ''}` : '',
        },
        {
          label: 'Roteiro',
          value: `${palavras}/${MAX_PALAVRAS_MODULO * 4} palavras`,
          hint: `≈ ${um(segundosFala(palavras))} s de fala em 20 s de vídeo`,
        },
      ]}
    >
      <Alert severity="info">
        Esta tela gera o vídeo e <b>gasta dinheiro</b> na API paga do Higgsfield (saldo em dólar da conta da Fiesta). Nada é gerado sem
        você ver a estimativa e confirmar. Cena, 4 módulos e montagem passam pela sua aprovação, um de cada vez.
      </Alert>

      <ProjetosSalvos atual={b.projetoId} onAbrir={abrirProjeto} />

      <Card>
        <CardContent>
          <Stepper nonLinear activeStep={etapa} orientation={estreito ? 'vertical' : 'horizontal'} sx={{ mb: 3 }}>
            {ETAPAS.map((rotulo, i) => (
              <Step key={rotulo} completed={feitas[i] && i !== etapa}>
                <StepButton color="inherit" onClick={() => setEtapa(i)}>
                  {rotulo}
                </StepButton>
              </Step>
            ))}
          </Stepper>

          {etapa === 0 ? <EtapaProduto b={b} set={set} /> : null}
          {etapa === 1 ? <EtapaRoteiro b={b} set={set} /> : null}
          {etapa === 2 ? <EtapaCena b={b} set={set} /> : null}
          {etapa === 3 ? (
            <Stack spacing={3}>
              <EtapaGeracao b={b} set={set} status={status} />
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<span aria-hidden>▾</span>}>
                  <Typography variant="subtitle2">Alternativa: gerar no Claude com o conector Higgsfield (créditos do plano)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EtapaPedido b={b} set={set} />
                </AccordionDetails>
              </Accordion>
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 3 }}>
            <Button
              color="inherit"
              onClick={() => {
                setB(briefingVazio())
                setEtapa(0)
              }}
            >
              Começar outro vídeo
            </Button>
            <Stack direction="row" spacing={1}>
              <Button disabled={etapa === 0} onClick={() => setEtapa((e) => e - 1)}>
                Voltar
              </Button>
              {etapa < ETAPAS.length - 1 ? (
                <Button variant="contained" onClick={() => setEtapa((e) => e + 1)}>
                  Próximo
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </DomainPageShell>
  )
}
